import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const MeshLicensePage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Harmony Mesh Network Participant License - SELF Labs</title>
        <meta name="description" content="Harmony Mesh Network Participant License - Effective Date: April 1, 2025" />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              📜 Harmony Mesh Network Participant License
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: April 1, 2025</p>

            <div className="prose prose-invert max-w-none mb-12">
              <p>This Participant License ("License") is entered into between SELF Labs Inc. ("SELF Labs," "we," "us," or "our") and the individual user ("Participant," "you," or "your") who activates Mesh Participation functionality via the SELF Phone.</p>
              <p>By participating in the Harmony Mesh Network, you agree to be bound by this License.</p>
            </div>

            <div className="space-y-12">
              {/* Purpose of License */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Purpose of License</h2>
                <div className="prose prose-invert max-w-none">
                  <p>The purpose of this License is to allow your SELF Phone to securely participate as a:</p>
                  <ul>
                    <li>Signal relay node</li>
                    <li>Agent task execution node</li>
                    <li>Encrypted data routing node</li>
                  </ul>
                  <p>in the decentralized Harmony Mesh Network, operated by SELF Labs.</p>
                  <p>Participation enables autonomous AI agent execution, encrypted task validation, and decentralized signal routing within the Signal Economy.</p>
                </div>
              </section>

              {/* Grant of License */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. Grant of License</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs grants you a limited, non-exclusive, non-transferable, revocable license to:</p>
                  <ul>
                    <li>Relay encrypted Harmony Signals</li>
                    <li>Execute Harmony Signal Engine™ validated tasks</li>
                    <li>Participate in encrypted data routing across the Mesh</li>
                    <li>Earn ESSENCE utility rewards based on valid participation</li>
                  </ul>
                  <p>This license applies solely to authorized operations as defined in Harmony Mesh protocols.</p>
                </div>
              </section>

              {/* Participant Responsibilities */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. Participant Responsibilities</h2>
                <div className="prose prose-invert max-w-none">
                  <p>By joining the Harmony Mesh, you agree to:</p>
                  <ul>
                    <li>Maintain the security and integrity of your SELF Phone.</li>
                    <li>Allow authorized Harmony Signals to be processed, routed, or executed securely.</li>
                    <li>Abstain from introducing unauthorized, manipulated, fraudulent, or malicious signals.</li>
                    <li>Operate your node in accordance with all current and future Harmony Mesh Network protocols.</li>
                  </ul>
                  <p>Participation is voluntary. You may disable Mesh Participation at any time via your SELF Phone settings.</p>
                </div>
              </section>

              {/* Prohibited Activities */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. Prohibited Activities</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Participants are expressly forbidden from:</p>
                  <ul>
                    <li>Introducing unauthorized signals, malware, viruses, or exploits into the Mesh.</li>
                    <li>Attempting to falsify relay counts, execution reports, or participation metrics.</li>
                    <li>Attempting to intercept, decrypt, manipulate, or analyze the content of Harmony Signals.</li>
                    <li>Disrupting Mesh operations through denial-of-service attacks, traffic flooding, or other malicious activities.</li>
                    <li>Using the Mesh Network for any purpose other than authorized Harmony Signal participation.</li>
                  </ul>
                  <p>Violation of these terms will result in immediate suspension or permanent termination of participation and forfeiture of accrued ESSENCE rewards.</p>
                  <p>SELF Labs reserves the right to pursue legal action for material breaches of this License.</p>
                </div>
              </section>

              {/* Protocol Updates */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. Protocol Updates</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs may update, upgrade, or modify Harmony Mesh Network protocols, including:</p>
                  <ul>
                    <li>Signal encryption standards</li>
                    <li>Execution validation methods</li>
                    <li>Node participation scoring</li>
                    <li>Reward distribution policies</li>
                  </ul>
                  <p>By participating, you consent to receive and apply all necessary protocol updates to maintain network security, performance, and functionality.</p>
                  <p>Continued participation after any update constitutes acceptance of the new protocol rules.</p>
                </div>
              </section>

              {/* Rewards and Compensation */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. Rewards and Compensation</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Participants may earn ESSENCE rewards for validated contributions to the Mesh, including:</p>
                  <ul>
                    <li>Relaying Harmony Signals</li>
                    <li>Successfully executing agent tasks</li>
                    <li>Supporting encrypted signal routing</li>
                  </ul>
                  <p>Reward rates are dynamic and based on network demand, signal load, participation volume, and other system-wide conditions.</p>
                  <p>Participation does not constitute employment, contractor status, or agency relationship with SELF Labs.</p>
                </div>
              </section>

              {/* Suspension and Termination */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Suspension and Termination</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs reserves the right to immediately suspend or terminate your Mesh participation if:</p>
                  <ul>
                    <li>You breach any term of this License</li>
                    <li>Your device is compromised, used for malicious activities, or introduces security risks</li>
                    <li>You fail to comply with updated Mesh Network protocols</li>
                  </ul>
                  <p>Suspension or termination may result in loss of future participation rights and forfeiture of associated rewards.</p>
                </div>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. Limitation of Liability</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs is not liable for any damages, losses, or claims arising from your participation in the Harmony Mesh Network, including but not limited to:</p>
                  <ul>
                    <li>Network downtime</li>
                    <li>Changes in ESSENCE reward values</li>
                    <li>Participation suspension or termination</li>
                    <li>Technical issues impacting node activity</li>
                  </ul>
                  <p>Participation is at your own risk.</p>
                </div>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">9. Governing Law</h2>
                <div className="prose prose-invert max-w-none">
                  <p>This License is governed by and construed in accordance with the laws of the State of Delaware, United States.</p>
                  <p>All disputes arising under this License shall be resolved through binding arbitration in Wilmington, Delaware.</p>
                </div>
              </section>

              {/* Acceptance */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">📋 Acceptance</h2>
                <div className="prose prose-invert max-w-none">
                  <p>By activating Mesh Participation on your SELF Phone, you acknowledge that you have read, understood, and agree to be legally bound by this Mesh Network Participant License.</p>
                </div>
              </section>
            </div>
          </motion.div>

          {/* Footer */}
          <div className="mt-20 pt-12 border-t border-indigo-500/20">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">SELF Labs Inc.</h3>
                  <p className="text-gray-400">Powered by Role Model AI + Aura Protocol</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Legal</h3>
                  <div className="space-y-2">
                    <Link to="/terms" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Terms & Conditions
                    </Link>
                    <Link to="/privacy" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Privacy Policy
                    </Link>
                    <Link to="/reseller-agreement" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Reseller Agreement
                    </Link>
                    <Link to="/mesh-license" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Mesh License
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Contact</h3>
                  <div className="space-y-2">
                    <a href="mailto:data@rolemodel.ai" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      data@rolemodel.ai
                    </a>
                    <a href="https://x.com/selflabsinc" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      @selflabsinc
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-indigo-500/20 text-center text-sm text-gray-400">
                <p>&copy; {new Date().getFullYear()} SELF Labs Inc. All rights reserved.</p>
                <p className="mt-2">Protected by PureCipher's OMNISEAL™ encryption suite</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeshLicensePage; 